class CompleteTheLook extends HTMLElement {
  constructor() {
    super();
    this.variantsData = null;
    this.initialized = false;
    this.debounceTimer = null;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      this.init();
    }
  }

  init() {
    this.loadVariantsData();
    this.bindEvents();
  }

  loadVariantsData() {
    const blockId = this.dataset.blockId;
    let scriptTag = null;
    
    if (blockId) {
      scriptTag = document.querySelector(`#complete-the-look-variants-${blockId}`);
    }
    
    if (!scriptTag) {
      scriptTag = this.querySelector('[id^="complete-the-look-variants-"]');
    }
    
    if (!scriptTag) {
      scriptTag = document.querySelector('[id^="complete-the-look-variants-"]');
    }
    
    if (scriptTag) {
      try {
        const dataText = scriptTag.textContent.trim();
        this.variantsData = JSON.parse(dataText);
      } catch (e) {
        // Silent fail - variants data not available
      }
    }
  }

  bindEvents() {
    // Use event delegation for better performance
    this.addEventListener('change', (e) => {
      if (e.target.matches('.complete-the-look__product-option-select')) {
        this.handleVariantChange(e.target);
      }
    });

    this.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('.complete-the-look__product-add-btn');
      const addAllBtn = e.target.closest('.complete-the-look__add-all-btn');
      
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuickAdd(quickAddBtn);
      } else if (addAllBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleAddAll();
      }
    });

    // Initialize variant states for products with auto-selected options
    requestAnimationFrame(() => {
      const productContainers = this.querySelectorAll('.complete-the-look__product');
      productContainers.forEach(container => {
        const productId = container.dataset.productId;
        const allSelects = container.querySelectorAll(`.complete-the-look__product-option-select[data-product-id="${productId}"]`);
        
        if (allSelects.length > 0) {
          Array.from(allSelects).forEach(select => {
            const optionName = (select.dataset.optionName || '').toLowerCase();
            const isSizeOption = optionName === 'size' || optionName === 'beden';
            
            if (select.value && select.value !== '' && isSizeOption) {
              this.handleVariantChange(select);
            } else if (select.dataset.autoSelect === 'true') {
              this.handleVariantChange(select);
            }
          });
        }
      });
      
      this.updateAddAllButtonState();
    });
  }

  handleVariantChange(select) {
    const productId = select.dataset.productId;
    
    if (!this.variantsData || !this.variantsData[productId]) return;

    const productContainer = select.closest('.complete-the-look__product');
    const variantIdInput = productContainer.querySelector(`.complete-the-look__product-variant-id[data-product-id="${productId}"]`);
    const quickAddBtn = productContainer.querySelector(`.complete-the-look__product-add-btn[data-product-id="${productId}"]`);

    // Get all selected options in order
    const selectedOptions = [];
    const allSelects = Array.from(productContainer.querySelectorAll(`.complete-the-look__product-option-select[data-product-id="${productId}"]`))
      .sort((a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10));
    
    let sizeOptionSelected = false;
    
    allSelects.forEach(s => {
      const optionName = (s.dataset.optionName || '').toLowerCase();
      const isSizeOption = optionName === 'size' || optionName === 'beden';
      
      let optionValue = '';
      
      if (s.selectedIndex >= 0 && s.options[s.selectedIndex]) {
        const selectedOption = s.options[s.selectedIndex];
        optionValue = selectedOption.value || '';
        
        if (!optionValue || selectedOption.disabled) {
          optionValue = '';
        }
      }
      
      if (!optionValue && s.dataset.autoSelect === 'true') {
        for (let i = 0; i < s.options.length; i++) {
          const opt = s.options[i];
          if (opt.value && opt.value !== '' && !opt.disabled) {
            optionValue = opt.value;
            break;
          }
        }
      }
      
      if (optionValue && optionValue !== '') {
        selectedOptions.push(optionValue);
        if (isSizeOption) {
          sizeOptionSelected = true;
        }
      }
    });

    if (!sizeOptionSelected) {
      variantIdInput.value = '';
      quickAddBtn.dataset.variantId = '';
      quickAddBtn.disabled = true;
      quickAddBtn.innerHTML = `<span>${this.getTranslation('product.complete_the_look.quick_add')}</span>`;
      this.updateAddAllButtonState();
      return;
    }

    // Find matching variant
    const variants = this.variantsData[productId];
    let matchingVariant = null;

    for (const variantId in variants) {
      const variant = variants[variantId];
      if (this.areOptionsMatching(variant.options, selectedOptions)) {
        matchingVariant = variant;
        break;
      }
    }

    if (matchingVariant) {
      variantIdInput.value = matchingVariant.id;
      quickAddBtn.dataset.variantId = matchingVariant.id;
      quickAddBtn.disabled = !matchingVariant.available;
      const buttonText = matchingVariant.available 
        ? this.getTranslation('product.complete_the_look.quick_add')
        : this.getTranslation('product.complete_the_look.sold_out');
      quickAddBtn.innerHTML = `<span>${buttonText}</span>`;

      // Update price
      const priceContainer = productContainer.querySelector('.complete-the-look__product-price');
      if (priceContainer && matchingVariant.price) {
        const priceElement = priceContainer.querySelector('.price__container');
        let moneyFormat = '{{amount}}';
        
        if (priceElement && priceElement.dataset.moneyFormat) {
          moneyFormat = priceElement.dataset.moneyFormat;
        } else if (window.Shopify?.money_format) {
          moneyFormat = window.Shopify.money_format;
        } else if (window.money_format) {
          moneyFormat = window.money_format;
        }

        if (window.Shopify?.formatMoney) {
          const formattedPrice = window.Shopify.formatMoney(matchingVariant.price, moneyFormat);
          priceContainer.innerHTML = formattedPrice;
        } else {
          const priceInCurrency = (matchingVariant.price / 100).toFixed(2);
          priceContainer.innerHTML = priceInCurrency.replace('.', ',') + ' TL';
        }
      }
    } else {
      variantIdInput.value = '';
      quickAddBtn.dataset.variantId = '';
      quickAddBtn.disabled = true;
      quickAddBtn.innerHTML = `<span>${this.getTranslation('product.complete_the_look.quick_add')}</span>`;
    }

    this.updateAddAllButtonState();
  }

  updateAddAllButtonState() {
    if (this.debounceTimer) {
      cancelAnimationFrame(this.debounceTimer);
    }
    
    this.debounceTimer = requestAnimationFrame(() => {
      const addAllBtn = this.querySelector('.complete-the-look__add-all-btn');
      if (!addAllBtn) return;
      
      const productContainers = this.querySelectorAll('.complete-the-look__product');
      let hasSelectedSize = false;
      
      productContainers.forEach(container => {
        const productId = container.dataset.productId;
        const variantIdInput = container.querySelector(`.complete-the-look__product-variant-id[data-product-id="${productId}"]`);
        const quickAddBtn = container.querySelector(`.complete-the-look__product-add-btn[data-product-id="${productId}"]`);
        const allSelects = container.querySelectorAll(`.complete-the-look__product-option-select[data-product-id="${productId}"]`);
        
        if (allSelects.length > 0) {
          allSelects.forEach(select => {
            const optionName = (select.dataset.optionName || '').toLowerCase();
            const isSizeOption = optionName === 'size' || optionName === 'beden';
            
            if (isSizeOption && select.value && select.value !== '') {
              hasSelectedSize = true;
            }
          });
        } else if (quickAddBtn && !quickAddBtn.disabled && variantIdInput?.value) {
          hasSelectedSize = true;
        }
      });

      if (hasSelectedSize) {
        addAllBtn.disabled = false;
        addAllBtn.innerHTML = `<span>${this.getTranslation('product.complete_the_look.add_all_available')}</span>`;
      } else {
        addAllBtn.disabled = true;
        addAllBtn.innerHTML = `<span>${this.getTranslation('product.complete_the_look.build_bundle')}</span>`;
      }
    });
  }

  areOptionsMatching(variantOptions, selectedOptions) {
    if (variantOptions.length !== selectedOptions.length) return false;
    for (let i = 0; i < variantOptions.length; i++) {
      if (variantOptions[i] !== selectedOptions[i]) return false;
    }
    return true;
  }

  getTranslation(key) {
    // Try to get from data attributes first
    const translations = this.dataset.translations ? JSON.parse(this.dataset.translations) : {};
    if (translations[key]) {
      return translations[key];
    }
    
    // Fallback to Shopify translation system
    if (window.Shopify?.locale?.strings?.[key]) {
      return window.Shopify.locale.strings[key];
    }
    
    // Final fallback
    const fallbacks = {
      'product.complete_the_look.quick_add': 'QUICK ADD',
      'product.complete_the_look.sold_out': 'Tükendi',
      'product.complete_the_look.add_all_available': 'TÜM MEVCUTLARI EKLE',
      'product.complete_the_look.build_bundle': 'KOMBİNİNİ OLUŞTUR: DEVAM ETMEK İÇİN BİR SEÇENEK SEÇ',
      'product.complete_the_look.no_items': 'Sepete eklenebilecek mevcut ürün yok'
    };
    return fallbacks[key] || key;
  }

  async handleQuickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.classList.add('is-loading');
    button.disabled = true;
    const originalText = button.innerHTML;

    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to cart failed');
      }

      await this.refreshCart();
      this.openCartDrawer();

    } catch (error) {
      alert(error.message || this.getTranslation('product.complete_the_look.no_items'));
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.innerHTML = originalText;
    }
  }

  async handleAddAll() {
    const addAllBtn = this.querySelector('.complete-the-look__add-all-btn');
    if (!addAllBtn || addAllBtn.disabled) {
      return;
    }
    
    const productContainers = this.querySelectorAll('.complete-the-look__product');
    const items = [];

    productContainers.forEach(container => {
      const productId = container.dataset.productId;
      const variantIdInput = container.querySelector(`.complete-the-look__product-variant-id[data-product-id="${productId}"]`);
      const quickAddBtn = container.querySelector(`.complete-the-look__product-add-btn[data-product-id="${productId}"]`);
      
      // Check if variant is selected and button is enabled
      if (variantIdInput?.value && quickAddBtn && !quickAddBtn.disabled) {
        const variantId = variantIdInput.value;
        
        // Verify variant is available
        if (this.variantsData?.[productId]) {
          const productVariants = this.variantsData[productId];
          const variant = productVariants[variantId] || productVariants[String(variantId)] || productVariants[parseInt(variantId, 10)];
          
          if (variant && (variant.available === true || variant.available === 'true' || variant.available === 1)) {
            items.push({
              id: parseInt(variantId, 10),
              quantity: 1
            });
          }
        } else {
          // If no variant data, trust the button state
          items.push({
            id: parseInt(variantId, 10),
            quantity: 1
          });
        }
      }
    });

    if (items.length === 0) {
      alert(this.getTranslation('product.complete_the_look.no_items'));
      return;
    }

    addAllBtn.classList.add('is-loading');
    addAllBtn.disabled = true;
    const originalText = addAllBtn.innerHTML;

    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add all to cart failed');
      }

      await this.refreshCart();
      this.openCartDrawer();

    } catch (error) {
      alert(error.message || this.getTranslation('product.complete_the_look.no_items'));
    } finally {
      addAllBtn.classList.remove('is-loading');
      addAllBtn.disabled = false;
      addAllBtn.innerHTML = originalText;
    }
  }

  async refreshCart() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    try {
      const sections = ['cart-drawer', 'cart-counter'];
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sections.join(',')}`);

      if (response.ok) {
        const data = await response.json();

        if (data['cart-drawer']) {
          const cartDrawerBody = document.querySelector('#CartDrawer-Body');
          if (cartDrawerBody) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
            const newContent = doc.querySelector('#CartDrawer-Body');
            if (newContent) {
              cartDrawerBody.innerHTML = newContent.innerHTML;
            }
          }
        }

        if (data['cart-counter']) {
          const cartCounterSection = document.querySelector('#shopify-section-cart-counter');
          if (cartCounterSection) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data['cart-counter'], 'text/html');
            const newCounter = doc.querySelector('#shopify-section-cart-counter');
            if (newCounter) {
              cartCounterSection.innerHTML = newCounter.innerHTML;
            }
          }
        }

        if (typeof updateCartCounters === 'function') {
          updateCartCounters();
        }

        if (typeof updateFreeShipping === 'function') {
          updateFreeShipping();
        }
      }
    } catch (error) {
      // Silent fail - cart refresh failed
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      requestAnimationFrame(() => {
        cartDrawer.open();
      });
    }
  }
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}
